import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    RiBookmark3Line,
    RiClapperboardLine,
    RiCloseLine,
    RiFilmLine,
    RiLoginBoxLine,
    RiLogoutBoxLine,
    RiSearch2Line,
    RiTv2Line,
    RiUserLine,
} from 'react-icons/ri';
import { useAuth } from '../context/auth/AuthContext';

const navLinks = [
    { to: '/movies', label: 'Movies', icon: RiFilmLine },
    { to: '/tv', label: 'TV Shows', icon: RiTv2Line },
    { to: '/watchlist', label: 'Watchlist', icon: RiBookmark3Line },
];

const Nav = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { authLoading, signOut, user } = useAuth();
    const [accountOpen, setAccountOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        setMenuOpen(false);
        setAccountOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        const handlePointerDown = (e) => {
            if (!(e.target instanceof Element) || !e.target.closest('[data-account-area]')) {
                setAccountOpen(false);
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, []);

    const handleSignOut = async () => {
        await signOut();
        navigate('/movies');
        setMenuOpen(false);
    };

    return (
        <>
            <div className="fixed inset-x-0 top-0 z-40 px-6 pt-4 sm:px-10 lg:px-16 xl:px-24">
                <header className="flex items-center justify-between gap-3">
                    {/* Logo — standalone, no pill */}
                    <NavLink to="/movies" className="flex w-fit flex-shrink-0 items-center gap-2.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-[0.75rem] border border-white/10 bg-white text-black">
                            <RiClapperboardLine size={16} />
                        </div>
                        <span className="hidden text-[0.95rem] font-semibold text-[var(--text-strong)] sm:block">moviesntv</span>
                    </NavLink>

                    {/* Desktop: compact right-aligned pill — nav + search icon + account */}
                    <div className="glass-pill hidden items-center gap-1 p-1.5 md:flex">
                        <nav className="flex items-center gap-1">
                            {navLinks.map((link) => (
                                <NavLink
                                    key={link.to}
                                    to={link.to}
                                    className={({ isActive }) => `tab-pill ${isActive ? 'tab-pill-active' : ''}`}
                                >
                                    {link.label}
                                </NavLink>
                            ))}
                        </nav>

                        <span className="mx-1 h-5 w-px bg-white/10" />

                        <NavLink
                            to="/search"
                            className={({ isActive }) => `flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-300 ${isActive ? 'bg-white text-black' : 'text-[var(--text-strong)] hover:bg-white/[0.06]'}`}
                            aria-label="Search"
                        >
                            <RiSearch2Line size={16} />
                        </NavLink>

                        <div className="relative" data-account-area>
                            {!authLoading && (
                                user ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setAccountOpen((v) => !v)}
                                            className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-black transition-transform duration-300 hover:scale-105"
                                            aria-label="Account menu"
                                        >
                                            <span className="text-xs font-bold">{(user.email || 'U').charAt(0).toUpperCase()}</span>
                                        </button>
                                        {accountOpen && (
                                            <div className="info-glass-panel absolute right-0 top-[calc(100%+0.6rem)] z-50 w-64 overflow-hidden rounded-[1.1rem]">
                                                <div className="flex flex-col items-center gap-2.5 px-4 pb-4 pt-5">
                                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-base font-bold text-black">
                                                        {(user.email || 'U').charAt(0).toUpperCase()}
                                                    </span>
                                                    <p className="max-w-full truncate text-sm text-[var(--text-secondary)]">{user.email}</p>
                                                </div>
                                                <div className="p-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={handleSignOut}
                                                        className="action-pill action-pill-active w-full justify-center text-sm"
                                                    >
                                                        <RiLogoutBoxLine size={15} />
                                                        Sign out
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <NavLink
                                        to="/login"
                                        className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--text-strong)] transition-colors duration-300 hover:bg-white/[0.06]"
                                        aria-label="Sign in"
                                        title="Sign in"
                                    >
                                        <RiUserLine size={16} />
                                    </NavLink>
                                )
                            )}
                        </div>
                    </div>

                    {/* Mobile: search + hamburger */}
                    <div className="glass-pill flex items-center gap-1.5 p-1.5 md:hidden">
                        <NavLink
                            to="/search"
                            className={({ isActive }) => `flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-300 ${isActive ? 'bg-white text-black' : 'text-[var(--text-strong)]'}`}
                            aria-label="Search"
                        >
                            <RiSearch2Line size={16} />
                        </NavLink>
                        <button
                            onClick={() => setMenuOpen((v) => !v)}
                            className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--text-strong)]"
                            aria-label="Open menu"
                        >
                            <span className="relative h-4 w-5">
                                <span className={`absolute left-0 top-0 block h-px w-5 bg-[var(--text-strong)] transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] ${menuOpen ? 'translate-y-[7px] rotate-45' : ''}`} />
                                <span className={`absolute left-0 top-[7px] block h-px w-5 bg-[var(--text-strong)] transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] ${menuOpen ? 'opacity-0' : 'opacity-100'}`} />
                                <span className={`absolute left-0 top-[14px] block h-px w-5 bg-[var(--text-strong)] transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] ${menuOpen ? '-translate-y-[7px] -rotate-45' : ''}`} />
                            </span>
                        </button>
                    </div>
                </header>
            </div>

            {/* Mobile menu overlay */}
            <div className={`fixed inset-0 z-50 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] md:hidden ${menuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}>
                <div className="absolute inset-0 bg-black/80 backdrop-blur-3xl" onClick={() => setMenuOpen(false)} />
                <div className={`sidebar-panel absolute inset-y-0 right-0 w-72 max-w-[88vw] transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] ${menuOpen ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'}`}>
                    <div className="flex h-full flex-col px-4 py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="flex h-9 w-9 items-center justify-center rounded-[0.75rem] border border-white/10 bg-white text-black">
                                    <RiClapperboardLine size={16} />
                                </div>
                                <span className="text-[0.95rem] text-[var(--text-strong)]">moviesntv</span>
                            </div>
                            <button
                                onClick={() => setMenuOpen(false)}
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-[var(--text)]"
                            >
                                <RiCloseLine size={20} />
                            </button>
                        </div>

                        <div className="mt-6">
                            <p className="sidebar-section-label mb-3">Navigation</p>
                            <nav className="space-y-2">
                                {navLinks.map(({ to, label, icon: Icon }) => (
                                    <NavLink
                                        key={to}
                                        to={to}
                                        onClick={() => setMenuOpen(false)}
                                        className={({ isActive }) => `sidebar-nav-item group ${isActive ? 'sidebar-nav-item-active' : ''}`}
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <div className={`flex h-[2.125rem] w-[2.125rem] items-center justify-center transition-colors duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${isActive ? 'text-black' : 'text-[var(--muted-warm)] group-hover:text-[var(--text)]'}`}>
                                                    <Icon size={17} />
                                                </div>
                                                <p className="min-w-0 flex-1 text-[0.95rem] text-inherit">{label}</p>
                                            </>
                                        )}
                                    </NavLink>
                                ))}
                            </nav>
                        </div>

                        <div className="mt-auto space-y-2 pt-6">
                            <p className="sidebar-section-label">Account</p>
                            {authLoading ? (
                                <div className="double-core px-3 py-3 text-sm text-[var(--text-secondary)]">Loading account...</div>
                            ) : user ? (
                                <div className="double-core rounded-[1rem] p-3.5">
                                    <div className="flex items-center gap-2.5">
                                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-black">
                                            {(user.email || 'U').charAt(0).toUpperCase()}
                                        </span>
                                        <p className="truncate text-sm text-[var(--text-secondary)]">{user.email || 'Signed in user'}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleSignOut}
                                        className="action-pill action-pill-active mt-3 w-full justify-center"
                                    >
                                        <RiLogoutBoxLine size={15} />
                                        Sign out
                                    </button>
                                </div>
                            ) : (
                                <NavLink
                                    to="/login"
                                    onClick={() => setMenuOpen(false)}
                                    className="action-pill w-full justify-center"
                                >
                                    <RiLoginBoxLine size={15} />
                                    Continue with email
                                </NavLink>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Nav;
